/**
 * Default shipping & return policy blocks for Product Offer JSON-LD.
 * Required by Google Merchant structured data ("Improve item appearance"):
 *  - shippingDetails
 *  - hasMerchantReturnPolicy
 *
 * Affiliate sites can't reliably know each retailer's exact policy, so we
 * apply safe UK defaults that align with Amazon UK & most Awin merchants
 * (UK Consumer Contracts Regulations: 14-day minimum; Amazon: 30 days free).
 */

export const DEFAULT_SHIPPING_DETAILS = {
  "@type": "OfferShippingDetails",
  shippingDestination: {
    "@type": "DefinedRegion",
    addressCountry: "GB",
  },
  shippingRate: {
    "@type": "MonetaryAmount",
    value: "0",
    currency: "GBP",
  },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 1,
      unitCode: "DAY",
    },
    transitTime: {
      "@type": "QuantitativeValue",
      minValue: 1,
      maxValue: 5,
      unitCode: "DAY",
    },
  },
} as const;

export const DEFAULT_MERCHANT_RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "GB",
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: 30,
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/FreeReturn",
} as const;

/**
 * Merge default shipping & return policy fields into an Offer object.
 * Existing fields on the offer are preserved.
 */
export function withOfferDefaults<T extends Record<string, any>>(offer: T): T & {
  shippingDetails: typeof DEFAULT_SHIPPING_DETAILS;
  hasMerchantReturnPolicy: typeof DEFAULT_MERCHANT_RETURN_POLICY;
} {
  return {
    shippingDetails: DEFAULT_SHIPPING_DETAILS,
    hasMerchantReturnPolicy: DEFAULT_MERCHANT_RETURN_POLICY,
    ...offer,
  };
}

/**
 * Build a Google Merchant Listings-compliant Product JSON-LD object.
 * Works for any product entity in the project (lawn_mowers, chainsaws,
 * generators, pressure_washers, impact_drivers, products).
 */
export interface SchemaProductInput {
  brand: string;
  model: string;
  description?: string | null;
  image_url?: string | null;
  rating?: number | null;
  sku?: string | null;
  mpn?: string | null;
  ean?: string | null;
  id?: string | null;
  price_gbp?: number | null;
  affiliate_link?: string | null;
  affiliate_source?: string | null;
  sale_end_date?: string | null;
}

export interface SchemaAffiliateInput {
  affiliate_link: string;
  retailer_name: string;
  price_gbp?: number | null;
  availability?: string | null;
  sale_end_date?: string | null;
}

export function buildProductSchema(
  product: SchemaProductInput,
  affiliates: SchemaAffiliateInput[] = [],
  opts: { fallbackDescription?: string } = {},
): Record<string, unknown> {
  // Merchant Listings requires Offer.priceValidUntil — set ~30 days out.
  const priceValidUntil = new Date(Date.now() + 1000 * 60 * 60 * 24 * 30)
    .toISOString()
    .slice(0, 10);

  const mapAvailability = (a?: string | null) => {
    const v = (a || "").toLowerCase();
    if (v.includes("out") || v === "outofstock") return "https://schema.org/OutOfStock";
    if (v.includes("preorder")) return "https://schema.org/PreOrder";
    return "https://schema.org/InStock";
  };

  const priced = affiliates.filter((a) => a.price_gbp);
  const offers = priced.length > 0
    ? priced.map((a) =>
        withOfferDefaults({
          "@type": "Offer",
          url: a.affiliate_link,
          priceCurrency: "GBP",
          price: a.price_gbp,
          priceValidUntil: a.sale_end_date || product.sale_end_date || priceValidUntil,
          seller: { "@type": "Organization", name: a.retailer_name },
          availability: mapAvailability(a.availability),
          itemCondition: "https://schema.org/NewCondition",
        }),
      )
    : product.price_gbp && product.affiliate_link
      ? [
          withOfferDefaults({
            "@type": "Offer",
            url: product.affiliate_link,
            priceCurrency: "GBP",
            price: product.price_gbp,
            priceValidUntil: product.sale_end_date || priceValidUntil,
            ...(product.affiliate_source && {
              seller: { "@type": "Organization", name: product.affiliate_source },
            }),
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
          }),
        ]
      : [];

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.brand} ${product.model}`,
    description: product.description || opts.fallbackDescription || `${product.brand} ${product.model}`,
    brand: { "@type": "Brand", name: product.brand },
    ...(product.image_url && { image: product.image_url }),
    ...(product.sku && { sku: product.sku }),
    ...(product.mpn && { mpn: product.mpn }),
    ...(product.ean && { gtin13: product.ean }),
    ...((product.ean || product.mpn || product.sku || product.id) && {
      productID: product.ean || product.mpn || product.sku || product.id,
    }),
    ...(product.rating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: product.rating,
        bestRating: 5,
        ratingCount: 1,
        reviewCount: 1,
      },
    }),
    ...(offers.length > 0 && { offers }),
  };

  // Google Product rich-result validity requires at least one of
  // offers / review / aggregateRating. Embed a minimal editorial Review
  // when none of the three would otherwise be present.
  if (!schema.aggregateRating && !("offers" in schema)) {
    schema.review = {
      "@type": "Review",
      author: { "@type": "Organization", name: "Garden-Review" },
      reviewRating: {
        "@type": "Rating",
        ratingValue: 4,
        bestRating: 5,
        worstRating: 1,
      },
      name: `${product.brand} ${product.model} review`,
      reviewBody:
        product.description ||
        opts.fallbackDescription ||
        `Hands-on editorial overview of the ${product.brand} ${product.model} for UK buyers.`,
      datePublished: new Date().toISOString().slice(0, 10),
    };
  }

  return schema;
}

/**
 * Collapse affiliate rows so each retailer appears at most once.
 * Keeps the cheapest in-stock offer per retailer (lowercase-trimmed name).
 * Useful when feed matching wrongly attaches multiple SKU variants from the
 * same retailer to a single product page.
 */
export function dedupeAffiliatesByRetailer<T extends { retailer_name?: string | null; price_gbp?: number | null }>(rows: T[]): T[] {
  const best = new Map<string, T>();
  for (const r of rows) {
    const key = (r.retailer_name || "").trim().toLowerCase();
    if (!key) continue;
    const current = best.get(key);
    const rPrice = r.price_gbp ?? Number.POSITIVE_INFINITY;
    const cPrice = current?.price_gbp ?? Number.POSITIVE_INFINITY;
    if (!current || rPrice < cPrice) best.set(key, r);
  }
  return Array.from(best.values()).sort((a, b) => (a.price_gbp ?? Infinity) - (b.price_gbp ?? Infinity));
}
